import { useMemo, useState, type CSSProperties } from "react";
import { ConfirmModal, showToast } from "../primitives";
import { useUpdateWrappedApi, type WrappedApiOperation } from "../../hooks/use-wrapped-apis";

/**
 * Sprint 16b — per-operation enable/disable for a wrapped API. GET tools are
 * always live (locked on); the owner toggles WRITE tools (POST/PUT/PATCH/DELETE)
 * individually. Enabling a DELETE op prompts a confirm. Bulk affordances enable
 * all non-destructive writes or disable all writes at once. Each change calls
 * update_wrapped_api with the full enabled set.
 */
export function OperationsTable({
  slug,
  operations,
  isOwner,
}: {
  slug: string;
  operations: WrappedApiOperation[];
  isOwner: boolean;
}) {
  const update = useUpdateWrappedApi();
  const [pendingDelete, setPendingDelete] = useState<WrappedApiOperation | null>(null);

  const enabledKeys = useMemo(
    () => new Set(operations.filter((o) => o.is_write && o.enabled).map((o) => o.operation_key)),
    [operations],
  );
  const writeOps = operations.filter((o) => o.is_write);

  async function commit(next: Set<string>) {
    try {
      await update.mutateAsync({ slug, enabled_operations: [...next] });
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't update operations", "error", true);
    }
  }

  function toggle(op: WrappedApiOperation) {
    const next = new Set(enabledKeys);
    if (next.has(op.operation_key)) {
      next.delete(op.operation_key);
      void commit(next);
    } else if (op.method === "DELETE") {
      setPendingDelete(op); // confirm before enabling a destructive op
    } else {
      next.add(op.operation_key);
      void commit(next);
    }
  }

  function confirmEnableDelete() {
    if (!pendingDelete) return;
    const next = new Set(enabledKeys);
    next.add(pendingDelete.operation_key);
    setPendingDelete(null);
    void commit(next);
  }

  function enableAllSafeWrites() {
    const next = new Set(enabledKeys);
    for (const o of writeOps) if (o.method !== "DELETE") next.add(o.operation_key);
    void commit(next);
  }

  function disableAllWrites() {
    void commit(new Set());
  }

  return (
    <div>
      {isOwner && writeOps.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <button type="button" onClick={enableAllSafeWrites} disabled={update.isPending} style={bulkBtn}>
            Enable all writes (except deletes)
          </button>
          <button type="button" onClick={disableAllWrites} disabled={update.isPending} style={bulkBtn}>
            Disable all writes
          </button>
        </div>
      )}

      {operations.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>No operations found in this spec.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {operations.map((op) => (
            <div key={op.name} style={row}>
              <span style={methodBadge(op.method)}>{op.method}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <code style={opName} title={op.name}>{op.name}</code>
                <div style={opSummary} title={`${op.method} ${op.path}`}>{op.summary}</div>
              </div>
              {op.is_write ? (
                <Toggle
                  on={op.enabled}
                  disabled={!isOwner || update.isPending}
                  onClick={() => toggle(op)}
                  label={op.enabled ? "Enabled" : "Disabled"}
                />
              ) : (
                <span style={alwaysOn} title="Read-only tools are always available">always on</span>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Enable a destructive operation?"
        message={`"${pendingDelete?.name}" performs a DELETE — it removes data on the upstream API. Enable it only if you trust the AI flows that can call it.`}
        confirmLabel="Enable DELETE op"
        variant="danger"
        onConfirm={confirmEnableDelete}
      />
    </div>
  );
}

function Toggle({ on, disabled, onClick, label }: { on: boolean; disabled: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        position: "relative",
        width: 36,
        height: 20,
        borderRadius: 10,
        border: "none",
        flexShrink: 0,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled && !on ? 0.55 : 1,
        background: on ? "var(--color-text-info)" : "var(--color-background-tertiary, var(--color-border-secondary))",
        transition: "background 0.12s",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: on ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 0.12s",
        }}
      />
    </button>
  );
}

const METHOD_COLORS: Record<string, { fg: string; bg: string }> = {
  GET: { fg: "var(--c-green-800, var(--color-text-secondary))", bg: "var(--c-green-50, var(--color-background-secondary))" },
  POST: { fg: "var(--color-text-info)", bg: "var(--c-blue-50, var(--color-background-secondary))" },
  PUT: { fg: "var(--c-amber-800, var(--color-text-secondary))", bg: "var(--c-amber-50, var(--color-background-secondary))" },
  PATCH: { fg: "var(--c-amber-800, var(--color-text-secondary))", bg: "var(--c-amber-50, var(--color-background-secondary))" },
  DELETE: { fg: "var(--c-red-600, var(--color-text-primary))", bg: "var(--c-red-50, var(--color-background-secondary))" },
};

function methodBadge(method: string): CSSProperties {
  const c = METHOD_COLORS[method] ?? { fg: "var(--color-text-secondary)", bg: "var(--color-background-secondary)" };
  return {
    fontSize: 10,
    fontWeight: 600,
    fontFamily: "var(--font-mono)",
    color: c.fg,
    background: c.bg,
    borderRadius: "var(--border-radius-sm)",
    padding: "2px 6px",
    width: 52,
    textAlign: "center",
    flexShrink: 0,
  };
}

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 0",
  borderBottom: "0.5px solid var(--color-border-secondary)",
};

const opName: CSSProperties = {
  fontSize: 12,
  fontFamily: "var(--font-mono)",
  color: "var(--color-text-primary)",
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const opSummary: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const alwaysOn: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  flexShrink: 0,
};

const bulkBtn: CSSProperties = {
  fontSize: 12,
  padding: "5px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
